import React from "react";
import { useDispatch, useSelector } from "react-redux";
import AppState from "../../../store/AppState";
import { selectIsModalOpen } from "../../../store/modal/ModalSelectors";
import { setModalState } from "../../../store/modal/ModalActions";
import { useIntl } from "react-intl";
import { Box, Typography } from "@material-ui/core";
import CustomDialog from "../../common/DialogContainer";
import { styled } from "@mui/material";
import {
  JobDetails,
  jobListScope,
} from "../../../store/businessRules-list/BusinessRulesListTypes";
import {
  selectedCategoriesId,
  resetSelectedCategoriesData,
} from "../../../store/businessRules-list/BusinessRulesListActions";
import { selectAutomationRuleCategories } from "../../../store/businessRules-list/BusinessRuleslistSelectors";
import { LoadStoreList } from "../../../store/store-list/StoreListActions";
import { selectStoreList } from "../../../store/store-list/StoreListSelectors";
import { LoadAllCatalogs } from "../../../store/catalog/CatalogActions";
import { selectAllCatalogs } from "../../../store/catalog/CatalogSelectors";
import { AllCatalog } from "../../../store/catalog/CatalogTypes";

const styleClasses = {
  listParent: "listParent",
  listChild: "listChild",
  label: "label",
};
const StyledUl = styled("ul")(({ theme }) => ({
  [`&.${styleClasses.listParent}`]: {
    //display: "table",
    listStyle: "none",
    padding: 0,
  },
}));

const StyledLi = styled("li")(({ theme }) => ({
  [`&.${styleClasses.listChild}`]: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottom: "1px solid #E8E8E8",
    fontSize: 16,
  },
}));

const StyledLabel = styled("label")(({ theme }) => ({
  [`&.${styleClasses.label}`]: {
    fontWeight: 600,
  },
}));

interface Props {
  data: JobDetails;
}
const JobListDetailsModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, "JobListDetails"),
  );
  // SM4A-3104: lookups used to resolve the job's target
  // (store / catalog / category names) the way legacy displays it.
  const storeList = useSelector(selectStoreList);
  const categoryDetails = useSelector(selectAutomationRuleCategories);
  const allCatalogs = useSelector(selectAllCatalogs);
  const [catalogsByStore, setCatalogsByStore] = React.useState<
    Record<string, AllCatalog[]>
  >({});
  const [storeQueue, setStoreQueue] = React.useState<string[]>([]);

  // Legacy (_formatTarget) dedupes scope entries by store/catalog/category.
  const scope = React.useMemo(() => {
    const seen = new Set<string>();
    return (props.data.scope || []).filter((scopeEntry) => {
      const key = `${scopeEntry.storeId}_${scopeEntry.catalogId}_${scopeEntry.categoryId}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [props.data.scope]);

  React.useEffect(() => {
    if (!isModalOpen || !scope?.length) {
      return;
    }
    dispatch(resetSelectedCategoriesData());
    dispatch(selectedCategoriesId(scope));
    if (!storeList.length) {
      dispatch(LoadStoreList());
    }
    // Catalog lookups load one store at a time because the store slice keeps
    // only the most recently fetched store's catalogs (allCatalogs).
    const uniqueStoreIds = Array.from(
      new Set(scope.map((scopeEntry) => scopeEntry.storeId)),
    ).filter((storeId) => !catalogsByStore[storeId]);
    setStoreQueue(uniqueStoreIds);
    if (uniqueStoreIds.length) {
      dispatch(LoadAllCatalogs(uniqueStoreIds[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, scope]);

  React.useEffect(() => {
    if (!storeQueue.length || !allCatalogs.length) {
      return;
    }
    const [currentStoreId, ...remaining] = storeQueue;
    setCatalogsByStore((prev) => ({ ...prev, [currentStoreId]: allCatalogs }));
    setStoreQueue(remaining);
    if (remaining.length) {
      dispatch(LoadAllCatalogs(remaining[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCatalogs]);

  const notApplicable = intl.formatMessage({
    id: "jobListDetailsModal.notApplicable",
    defaultMessage: "NA",
  });

  // "Store / Catalog / Category" — falls back to raw ids while lookups load.
  const targetLabel = (scopeEntry: jobListScope) => {
    const storeName =
      storeList.find((store) => store.storeId === scopeEntry.storeId)?.name ||
      scopeEntry.storeId;
    const catalogName =
      catalogsByStore[scopeEntry.storeId]?.find(
        (catalog) => catalog.id === scopeEntry.catalogId,
      )?.name || scopeEntry.catalogId;
    const category = categoryDetails.find(
      (categoryData) => categoryData.categoryId === scopeEntry.categoryId,
    );
    const categoryName =
      category?.name || category?.code || scopeEntry.categoryId;
    return `${storeName} / ${catalogName} / ${categoryName}`;
  };

  const jobListDetailsModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "jobListDetailsModal.header",
          defaultMessage: "Job Details",
        })}
      </Typography>
    );
  };

  const jobListDetailsModalContent = () => {
    return (
      <>
        <Box>
          <StyledUl className={styleClasses.listParent}>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.description",
                  defaultMessage: "Description",
                })}
              </StyledLabel>
              <span>
                {props.data.description
                  ? props.data.description
                  : notApplicable}
              </span>
            </StyledLi>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.actualStartTime",
                  defaultMessage: "Actual Start Time",
                })}
              </StyledLabel>
              <span>
                {props.data.startTime ? props.data.startTime : notApplicable}
              </span>
            </StyledLi>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.endTime",
                  defaultMessage: "End Time",
                })}
              </StyledLabel>
              <span>
                {props.data.endTime ? props.data.endTime : notApplicable}
              </span>
            </StyledLi>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.target",
                  defaultMessage: "Target",
                })}
              </StyledLabel>
              <span style={{ textAlign: "right" }}>
                {scope?.length
                  ? scope.map((scopeEntry, index) => (
                      <div key={scopeEntry.categoryId || index}>
                        {targetLabel(scopeEntry)}
                        {index < scope.length - 1 ? "," : ""}
                      </div>
                    ))
                  : notApplicable}
              </span>
            </StyledLi>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.retries",
                  defaultMessage: "Retries",
                })}
              </StyledLabel>
              <span>
                {props.data.retries !== undefined && props.data.retries !== ""
                  ? intl.formatMessage(
                      {
                        id: "jobListDetailsModal.retriesValue",
                        defaultMessage: "{count} times",
                      },
                      { count: props.data.retries },
                    )
                  : notApplicable}
              </span>
            </StyledLi>
            <StyledLi className={styleClasses.listChild}>
              <StyledLabel className={styleClasses.label}>
                {intl.formatMessage({
                  id: "jobListDetailsModal.retryDelay",
                  defaultMessage: "Retry Delay",
                })}
              </StyledLabel>
              <span>
                {props.data.retryDelay !== undefined
                  ? intl.formatMessage(
                      {
                        id: "jobListDetailsModal.retryDelayValue",
                        defaultMessage: "{count} minutes",
                      },
                      { count: props.data.retryDelay },
                    )
                  : notApplicable}
              </span>
            </StyledLi>
          </StyledUl>
        </Box>
      </>
    );
  };

  const handleClose = () => {
    dispatch(resetSelectedCategoriesData());
    dispatch(setModalState("JobListDetails", false));
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={handleClose}
      title={jobListDetailsModalTitle()}
      fullWidth
      maxWidth={"sm"}
    >
      {jobListDetailsModalContent()}
    </CustomDialog>
  );
};

export default JobListDetailsModal;
